import { useQuery } from '@tanstack/react-query'

import { businessDayQueryOptions } from '@/api/reference-api'
import { session } from '@/api/session'
import type { BusinessDay } from '@/api/types'
import { useAuth } from '@/auth/use-auth'
import type { ApiError } from '@/lib/problem-details'

/**
 * The branch's business day, as the one fact every teller screen reads first.
 *
 * Two things about it are easy to get wrong and expensive to get wrong.
 *
 * **`businessDate` is nullable.** At seven in the morning, before anyone has opened the day, the
 * status is `NotOpened` and the date is `null`. That is ordinary, not a fault. Nothing here
 * substitutes today's date from the browser: a screen that did would show a teller a day they
 * cannot post into as though they could, and would stamp a filter with a date the ledger has never
 * heard of. Every consumer of this hook gets `businessDate: string | null` and has to say something
 * about the null case.
 *
 * **`acceptsPostings` is sent rather than derived.** It is the server's own answer to "would a
 * posting made now be accepted", and it is not the same question as "is the status Open" — a day
 * that is `Closing` has a settled date and takes nothing new. Guessing at it either blocks real
 * work or invites a transaction the ledger refuses after the customer has handed over cash.
 */
export interface PostingDay {
  /** The branch every read and write on the screen is scoped to. Null before the session resolves. */
  branchId: string | null
  /** The ledger's date, or null where no day has been opened. Never the browser's date. */
  businessDate: string | null
  status: BusinessDay['status'] | null
  /** The last day this branch traded, sent only when nothing is open. */
  lastClosedBusinessDate: string | null
  /** The server's own answer. False while the query is still in flight — postings fail closed. */
  acceptsPostings: boolean
  isPending: boolean
  isError: boolean
  error: ApiError | null
  refetch: () => void
}

export function usePostingDay(): PostingDay {
  const { user } = useAuth()
  const branchId = session.getBranchId() ?? user?.homeBranchId ?? null
  const query = useQuery(businessDayQueryOptions(branchId))

  return {
    branchId,
    businessDate: query.data?.businessDate ?? null,
    status: query.data?.status ?? null,
    lastClosedBusinessDate: query.data?.lastClosedBusinessDate ?? null,
    acceptsPostings: query.data?.acceptsPostings ?? false,
    isPending: query.isPending,
    isError: query.isError,
    error: query.isError ? query.error : null,
    refetch: () => void query.refetch(),
  }
}
